import type { YouTubeVideoSettings } from '@/types/youtube';

/**
 * YouTube's text limits, counted the way YouTube counts them — mirrors
 * `src/utils/youtube-text.util.ts` on the backend, so a field goes red at the
 * same moment the server would refuse it.
 */

export const TITLE_MAX = 100;
export const DESCRIPTION_MAX_BYTES = 5000;
export const TAGS_MAX = 500;
export const COMMENT_MAX = 10000;

const encoder = new TextEncoder();

export function utf8Bytes(text: string): number {
  return encoder.encode(text ?? '').length;
}

export function titleLength(title: string): number {
  return Array.from(title ?? '').length;
}

export function hasAngleBrackets(text: string): boolean {
  return /[<>]/.test(text ?? '');
}

/** Commas between tags count; a tag with a space is quoted, and the quotes count. */
export function tagsLength(tags: string[]): number {
  const clean = (tags ?? []).map((tag) => tag.trim()).filter(Boolean);
  if (!clean.length) return 0;
  return (
    clean.reduce(
      (sum, tag) => sum + Array.from(tag).length + (/\s/.test(tag) ? 2 : 0),
      0
    ) +
    (clean.length - 1)
  );
}

/** A title from a file name: extension gone, underscores to spaces. */
export function titleFromFileName(name: string): string {
  const base = (name ?? '')
    .replace(/\.[^./\\]+$/, '')
    .replace(/_+/g, ' ')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return Array.from(base).slice(0, TITLE_MAX).join('').trim();
}

/** A title from the first line of some text, for the Shorts panel. */
export function titleFromText(text: string): string {
  const first =
    (text ?? '')
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean) ?? '';
  const cleaned = first.replace(/[<>]/g, '').replace(/\s+/g, ' ').trim();
  const chars = Array.from(cleaned);
  if (chars.length <= TITLE_MAX) return cleaned;
  const cut = chars.slice(0, TITLE_MAX).join('');
  const space = cut.lastIndexOf(' ');
  return (space > 60 ? cut.slice(0, space) : cut).trim();
}

/** Whether YouTube will make this a Short: square or vertical, three minutes or less. */
export function looksLikeShort(
  width?: number | null,
  height?: number | null,
  durationSeconds?: number | null
): boolean {
  if (!width || !height) return false;
  if (durationSeconds != null && durationSeconds > 180) return false;
  return height >= width;
}

/**
 * Why these settings cannot be published, or null. The same rules
 * `youtubeSettingsReason` applies on the server.
 */
export function youtubeSettingsProblem(
  settings: YouTubeVideoSettings,
  description: string,
  appAudited: boolean
): string | null {
  const title = settings.title.trim();
  if (!title) return 'Add a title.';
  if (titleLength(title) > TITLE_MAX)
    return `The title is over ${TITLE_MAX} characters.`;
  if (hasAngleBrackets(title)) return 'Titles cannot contain < or >.';
  if (utf8Bytes(description) > DESCRIPTION_MAX_BYTES) {
    return `The description is over YouTube’s ${DESCRIPTION_MAX_BYTES.toLocaleString()}-byte limit.`;
  }
  if (hasAngleBrackets(description))
    return 'Descriptions cannot contain < or >.';
  if (tagsLength(settings.tags) > TAGS_MAX) {
    return `Tags are over ${TAGS_MAX} characters, counting commas and quotes.`;
  }
  if (settings.madeForKids == null) {
    return 'Say whether the video is made for kids.';
  }
  if (!appAudited && settings.privacyStatus !== 'private') {
    return 'Until our YouTube app passes YouTube’s audit, videos can only be uploaded as Private.';
  }
  if (settings.firstComment?.trim() && settings.madeForKids) {
    return 'Videos made for kids take no comments, so the first comment cannot be posted.';
  }
  const translations = Object.entries(settings.localizations ?? {});
  if (translations.length && !settings.defaultLanguage) {
    return 'Choose the title and description language before adding translations.';
  }
  for (const [language, entry] of translations) {
    if (!entry.title.trim())
      return `The ${language} translation needs a title.`;
  }
  return null;
}

export interface Chapter {
  seconds: number;
  label: string;
}

export function formatChapterTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function parseChapterTime(value: string): number | null {
  const parts = value
    .trim()
    .split(':')
    .map((part) => Number(part));
  if (
    !parts.length ||
    parts.some((part) => !Number.isFinite(part) || part < 0)
  ) {
    return null;
  }
  return parts.reduce((total, part) => total * 60 + part, 0);
}

/**
 * Why a chapter list will not show as chapters, or null. YouTube's rules:
 * the first starts at 0:00, there are at least three, each at least ten
 * seconds long.
 */
export function chaptersProblem(chapters: Chapter[]): string | null {
  if (!chapters.length) return null;
  const sorted = [...chapters].sort((a, b) => a.seconds - b.seconds);
  if (sorted[0].seconds !== 0) return 'The first chapter must start at 0:00.';
  if (sorted.length < 3) return 'YouTube needs at least three chapters.';
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].seconds - sorted[i - 1].seconds < 10) {
      return 'Each chapter must be at least 10 seconds long.';
    }
  }
  if (sorted.some((chapter) => !chapter.label.trim())) {
    return 'Give every chapter a name.';
  }
  return null;
}

export function chaptersToText(chapters: Chapter[]): string {
  return [...chapters]
    .sort((a, b) => a.seconds - b.seconds)
    .map(
      (chapter) =>
        `${formatChapterTime(chapter.seconds)} ${chapter.label.trim()}`
    )
    .join('\n');
}

export const PRIVACY_LABELS: Record<string, string> = {
  private: 'Private',
  unlisted: 'Unlisted',
  public: 'Public',
};

export const PRIVACY_HINTS: Record<string, string> = {
  private: 'Only you and people you choose can watch your video',
  unlisted: 'Anyone with the video link can watch your video',
  public: 'Everyone can watch your video',
};

const GOOGLE_IMAGE_HOSTS = ['ggpht.com', 'googleusercontent.com', 'ytimg.com'];

/**
 * Google's image hosts refuse some referrers — `yt3.ggpht.com` answers a
 * `localhost` page with 429 — and serve anyone who sends none. Other hosts
 * keep the browser's default.
 */
export function imageReferrerPolicy(
  url?: string | null
): 'no-referrer' | undefined {
  if (!url) return undefined;
  try {
    const host = new URL(url).hostname;
    return GOOGLE_IMAGE_HOSTS.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`)
    )
      ? 'no-referrer'
      : undefined;
  } catch {
    return undefined;
  }
}

/** The Studio edit page for a video, for everything the API cannot change. */
export function studioUrl(videoId: string): string {
  return `https://studio.youtube.com/video/${videoId}/edit`;
}
